import {
  OrdersHourlyReportDatum,
  PaymentsSummaryFilters,
  PaymentsSummaryReport,
  RevenueDatum,
  RevenueFilters,
  TipsDatum,
  TopCategoriesDatum,
  TopProductsDatum,
  WeeklyPerformanceDatum,
  WeeklyPerformanceFilters,
  OrdersBreakdown,
} from "@lemmonhq/types";
import { DateRange } from "@app/vue/components/index";
import { getAPIClient, routingFetch } from "../helpers";

// The overview report endpoints accept an optional list of user ids to scope
// the numbers to specific staff. Serialized as a comma-separated query param.
const serializeUserIds = (userIds?: number[]): string | undefined =>
  userIds && userIds.length > 0 ? userIds.join(",") : undefined;

export const reportsAPI = {
  getClosingReports: async (attrs: {
    page: number;
    limit: number;
    dateRange: DateRange;
    userId?: number;
  }) => {
    const hc = getAPIClient();
    const resp = await hc.api.v2.closing_reports.$get({
      query: {
        page: attrs.page,
        limit: attrs.limit,
        start_date: attrs.dateRange.startDate,
        end_date: attrs.dateRange.endDate,
      },
    });
    if (resp.ok) {
      const json = await resp.json();
      return json;
    } else {
      const json = await resp.json();
      throw new Error("unable to  find");
    }
  },
  // todo add proper parametees
  createClosingReport: async (attrs: {
    starting_balances: any;
    closing_balances: any;
    differences: any;
    notes: string;
    shift_id: string;
  }) => {
    const hc = getAPIClient();
    console.log("hc", hc);
    const resp = await hc.api.v2.closing_reports.$post({
      json: {
        starting_balances: attrs.starting_balances,
        closing_balances: attrs.closing_balances,
        differences: attrs.differences,
        notes: attrs.notes,
        date: new Date().toISOString(),
        shift_id: attrs.shift_id,
      },
    });
    console.log("r", resp);
    if (resp.ok) {
      const json = await resp.json();
      return json;
    } else {
      const json = await resp.json();
      throw new Error("unable to  create");
    }
  },
  deleteClosingReport: async (id: string) => {
    const response = await routingFetch(`/api/v2/shop/closing_reports/${id}`, {
      method: "DELETE",
    });
    if (!response.ok) throw new Error("Failed to delete closing report");
    return response.json();
  },
  getOrdersHourlyBreakdown: async (
    range: DateRange,
    userIds?: number[],
  ): Promise<OrdersHourlyReportDatum[]> => {
    const hc = getAPIClient();
    const p_user_ids = serializeUserIds(userIds);
    const response = await hc.api.reports["hourly-breakdown"].$get({
      query: {
        startDate: range.startDate,
        endDate: range.endDate,
        ...(p_user_ids ? { p_user_ids } : {}),
      },
    });
    if (!response.ok) {
      throw new Error("Failed to getOrdersHourlyBreakdown");
    }
    return response.json();
  },
  getOrdersBreakdown: async (
    range: DateRange,
    shift_id?: string,
    userIds?: number[],
  ): Promise<OrdersBreakdown> => {
    const hc = getAPIClient();
    const filters: Record<string, string> = {
      p_start_date: range.startDate,
      p_end_date: range.endDate,
    };
    if (shift_id) {
      filters.p_shift_id = shift_id;
    }
    const p_user_ids = serializeUserIds(userIds);
    if (p_user_ids) {
      filters.p_user_ids = p_user_ids;
    }
    const response = await hc.api.reports["orders-overview"].$get({
      query: filters as any,
    });
    if (!response.ok) {
      throw new Error("Failed to getOrdersBreakdown");
    }
    return response.json();
  },
  getTopProducts: async (
    range: DateRange,
    userIds?: number[],
  ): Promise<TopProductsDatum[]> => {
    const hc = getAPIClient();
    const p_user_ids = serializeUserIds(userIds);
    const response = await hc.api.reports["top-products"].$get({
      query: {
        startDate: range.startDate,
        endDate: range.endDate,
        ...(p_user_ids ? { p_user_ids } : {}),
      },
    });
    if (!response.ok) {
      throw new Error("Failed to fetch getTopProducts");
    }
    return response.json();
  },
  getTopCategories: async (
    range: DateRange,
    userIds?: number[],
  ): Promise<TopCategoriesDatum[]> => {
    const hc = getAPIClient();
    const p_user_ids = serializeUserIds(userIds);
    const response = await hc.api.reports["top-categories"].$get({
      query: {
        startDate: range.startDate,
        endDate: range.endDate,
        ...(p_user_ids ? { p_user_ids } : {}),
      },
    });
    if (!response.ok) {
      throw new Error("Failed getTopCategories");
    }
    return response.json();
  },

  getRevenueData: async (filters: RevenueFilters): Promise<RevenueDatum[]> => {
    const hc = getAPIClient();
    const response = await hc.api.reports.revenue.$get({
      query: filters as any,
    });
    if (!response.ok) {
      throw new Error("Failed to getRevenueData");
    }
    const list: RevenueDatum[] = await response.json();
    return list;
  },

  getWeeklyPerformance: async (
    filters: WeeklyPerformanceFilters,
  ): Promise<WeeklyPerformanceDatum[]> => {
    const hc = getAPIClient();
    const response = await hc.api.reports.weekly_performance.$get({
      query: {
        filters: btoa(JSON.stringify(filters)),
      },
    });
    if (!response.ok) {
      throw new Error("Failed to getWeeklyPerformance");
    }
    const list: WeeklyPerformanceDatum[] = await response.json();
    return list;
  },

  getTips: async (filters: PaymentsSummaryFilters): Promise<TipsDatum[]> => {
    const hc = getAPIClient();
    const response = await hc.api.reports.tips.$get({
      query: filters as any,
    });
    if (!response.ok) {
      throw new Error("Failed to fetch tips");
    }
    return response.json();
  },
  getPaymentsSummary: async (
    filters: PaymentsSummaryFilters,
    userIds?: number[],
  ): Promise<PaymentsSummaryReport> => {
    const hc = getAPIClient();
    const p_user_ids = serializeUserIds(userIds);
    const query = {
      ...JSON.parse(JSON.stringify(filters)),
      ...(p_user_ids ? { p_user_ids } : {}),
    };
    const response = await hc.api.reports.payments_summary.$get({
      query,
    });
    if (!response.ok) {
      throw new Error("Failed to getPaymentsSummary");
    }
    const list: PaymentsSummaryReport = await response.json();
    return list;
  },
};
